import { cssMerge } from '@shared/util';
import {
  type ElementType,
  forwardRef,
  type HTMLAttributes,
  type PropsWithChildren,
} from 'react';

type BasePosition = 'start' | 'end' | 'center';

export type Props = HTMLAttributes<Omit<HTMLDivElement, 'width' | 'color'>> &
  PropsWithChildren<{
    flex?: number;
    color?: Color;
    m?: BaseSize | 4;
    p?: BaseSize | 4 | 12;
    mx?: BaseSize | 4;
    px?: BaseSize | 4 | 12;
    py?: BaseSize | 4 | 12;
    my?: BaseSize | 4;
    pt?: BaseSize | 4;
    pb?: BaseSize | 4;
    mt?: BaseSize | 4;
    mb?: BaseSize | 4;
    gutterX?: boolean;
    bgcolor?: BgColor;
    invisible?: boolean;
    fullWidth?: boolean;
    component?: ElementType;
    gap?: BaseSize | 4 | 96 | 128;
    alignItems?: BasePosition | 'stretch';
    container?: boolean | 'max' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';
    display?: 'flex' | 'grid' | 'block' | 'inline' | 'contents' | 'flow-root';
    justifyContent?:
      | BasePosition
      | 'space-evenly'
      | 'space-around'
      | 'space-between';
    width?:
      | BaseSize
      | 40
      | 'xs'
      | 'sm'
      | 'md'
      | 'lg'
      | 'xl'
      | 'min'
      | 'fit'
      | 'full';
  }>;

const Box = forwardRef<HTMLDivElement, Props>(
  (
    {
      m,
      p,
      mt,
      mb,
      pt,
      pb,
      mx,
      my,
      px,
      py,
      gap,
      flex,
      color,
      style,
      width,
      hidden,
      bgcolor,
      display,
      gutterX,
      children,
      className,
      container,
      fullWidth,
      invisible,
      alignItems,
      justifyContent,
      component: Component = 'div',
      ...restProps
    },
    ref,
  ) => (
    <Component
      ref={ref}
      {...restProps}
      style={{ flex, ...style }}
      className={cssMerge(
        display,
        className,
        hidden && 'hidden',
        fullWidth && 'w-full',
        invisible && 'invisible',
        color && `text-${color}`,
        gutterX && 'gap-x-gutter',
        bgcolor && `bg-${bgcolor}`,
        m !== undefined && `m-${m}`,
        p !== undefined && `p-${p}`,
        mb !== undefined && `mb-${mb}`,
        mt !== undefined && `mt-${mt}`,
        mx !== undefined && `mx-${mx}`,
        my !== undefined && `my-${my}`,
        pb !== undefined && `pb-${pb}`,
        pt !== undefined && `pt-${pt}`,
        px !== undefined && `px-${px}`,
        py !== undefined && `py-${py}`,
        gap !== undefined && `gap-${gap}`,
        container === true && 'container',
        width !== undefined && `w-${width}`,
        alignItems && `items-${alignItems}`,
        justifyContent && `justify-${justifyContent.replace('space-', '')}`,
        container && typeof container === 'string' && `container-${container}`,
      )}
    >
      {children}
    </Component>
  ),
);

Box.displayName = 'Box';

export default Box;
